// MCHD Room Scheduler - Session Time List Component
// Displays paginated list of room session times with filtering and actions

import React, { useState } from 'react';
import {
  Paper,
  Table,
  TextInput,
  Select,
  Group,
  Button,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Pagination,
  Stack,
  Alert,
  Center,
  Loader,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconDots,
  IconEdit,
  IconTrash,
  IconClock,
  IconCheck,
  IconX,
  IconAlertCircle
} from '@tabler/icons-react';

// Import types
import type { 
  RoomSessionTimeWithDetails, 
  PaginationMeta,
  Building,
  Room 
} from '../../api/endpoints/session-times';
import { formatSessionDuration, getSessionTimeStatusColor } from '../../api/endpoints/session-times';

// =============================================================================
// INTERFACES
// =============================================================================

interface SessionTimeListProps {
  sessionTimes: RoomSessionTimeWithDetails[];
  pagination?: PaginationMeta | undefined;
  isLoading?: boolean | undefined;
  error?: any;
  buildings: Building[];
  rooms: Room[];
  filters: {
    search: string;
    roomId?: string | undefined;
    buildingId?: string | undefined;
    isActive?: boolean | undefined;
    sortBy: 'name' | 'startTime' | 'endTime' | 'createdAt' | 'updatedAt';
    sortOrder: 'asc' | 'desc';
  };
  onFilterChange: (filters: Partial<SessionTimeListProps['filters']>) => void;
  onSelectSessionTime: (sessionTime: RoomSessionTimeWithDetails) => void;
  onDeleteSessionTime: (id: string) => void;
  onUpdateSessionTime: (id: string, data: any) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Session Time List Component
 * Displays paginated list of room session times with filtering and actions
 */
export function SessionTimeList({
  sessionTimes,
  pagination,
  isLoading,
  error,
  buildings,
  rooms,
  filters,
  onFilterChange,
  onSelectSessionTime,
  onDeleteSessionTime,
  onUpdateSessionTime: _onUpdateSessionTime
}: SessionTimeListProps) {
  // =============================================================================
  // STATE
  // =============================================================================

  const [searchValue, setSearchValue] = useState(filters.search);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle search input change
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setSearchValue(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      onFilterChange({ search: value });
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  /**
   * Handle building filter change
   */
  const handleBuildingFilterChange = (buildingId: string | null) => {
    onFilterChange({ 
      ...(buildingId && { buildingId })
      // roomId will be reset by parent component
    });
  };

  /**
   * Handle room filter change
   */
  const handleRoomFilterChange = (roomId: string | null) => {
    onFilterChange({ ...(roomId && { roomId }) });
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (status: string | null) => {
    let isActive: boolean | undefined;
    if (status === 'active') isActive = true;
    else if (status === 'inactive') isActive = false;
    else isActive = undefined;
    
    onFilterChange({ ...(isActive !== undefined && { isActive }) });
  };

  /**
   * Handle sort change
   */
  const handleSortChange = (sortBy: string) => {
    const newSortOrder = filters.sortBy === sortBy && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    onFilterChange({ sortBy: sortBy as any, sortOrder: newSortOrder });
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page: number) => {
    // This would be handled by the parent component
    console.log('Page changed to:', page);
  };

  // =============================================================================
  // FILTERED ROOMS
  // =============================================================================

  const filteredRooms = filters.buildingId 
    ? rooms.filter(room => room.floor.building.id === filters.buildingId)
    : rooms;

  // =============================================================================
  // LOADING STATE
  // =============================================================================

  if (isLoading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Loader size="md" />
          <Text c="dimmed">Loading session times...</Text>
        </Stack>
      </Center>
    );
  }

  // =============================================================================
  // ERROR STATE
  // =============================================================================

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Failed to Load Session Times"
        color="red"
        variant="light"
      >
        {error?.message || 'An error occurred while loading session times'}
      </Alert>
    );
  }

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Stack gap="md">
      {/* Filters */}
      <Paper p="md" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-end">
            <Text fw={600} size="sm">
              Filters
            </Text>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFilter size={14} />}
              onClick={() => onFilterChange({ search: '' })}
            >
              Clear All
            </Button>
          </Group>
          
          <Group gap="md" align="flex-end">
            {/* Search */}
            <TextInput
              placeholder="Search session times..."
              leftSection={<IconSearch size={16} />}
              value={searchValue}
              onChange={handleSearchChange}
              style={{ flex: 1 }}
            />
            
            {/* Building Filter */}
            <Select
              placeholder="All Buildings"
              data={[
                { value: '', label: 'All Buildings' },
                ...buildings.map(building => ({
                  value: building.id,
                  label: building.name
                }))
              ]}
              value={filters.buildingId || ''}
              onChange={handleBuildingFilterChange}
              clearable
              style={{ minWidth: 150 }}
            />
            
            {/* Room Filter */}
            <Select
              placeholder="All Rooms"
              data={[
                { value: '', label: 'All Rooms' },
                ...filteredRooms.map(room => ({
                  value: room.id,
                  label: `${room.number} - ${room.name}`
                }))
              ]}
              value={filters.roomId || ''}
              onChange={handleRoomFilterChange}
              clearable
              disabled={!filters.buildingId}
              style={{ minWidth: 150 }}
            />
            
            {/* Status Filter */}
            <Select
              placeholder="All Status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              value={filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : ''}
              onChange={handleStatusFilterChange}
              clearable
              style={{ minWidth: 120 }}
            />
          </Group>
        </Stack>
      </Paper>

      {/* Session Times Table */}
      <Paper radius="md" withBorder>
        <Table.ScrollContainer minWidth={800}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => handleSortChange('name')}
                    rightSection={
                      filters.sortBy === 'name' ? (
                        <Text size="xs">{filters.sortOrder === 'asc' ? '↑' : '↓'}</Text>
                      ) : null
                    }
                  >
                    Session Name
                  </Button>
                </Table.Th>
                <Table.Th>Room</Table.Th>
                <Table.Th>Building</Table.Th>
                <Table.Th>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => handleSortChange('startTime')}
                    rightSection={
                      filters.sortBy === 'startTime' ? (
                        <Text size="xs">{filters.sortOrder === 'asc' ? '↑' : '↓'}</Text>
                      ) : null
                    }
                  >
                    Time
                  </Button>
                </Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Buffer</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ width: 80 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            
            <Table.Tbody>
              {sessionTimes.length > 0 ? (
                sessionTimes.map((sessionTime) => (
                  <Table.Tr key={sessionTime.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconClock size={16} />
                        <Text fw={500} size="sm">
                          {sessionTime.name}
                        </Text>
                      </Group>
                    </Table.Td>
                    
                    <Table.Td>
                      <Text size="sm">
                        {sessionTime.room.number}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {sessionTime.room.name}
                      </Text>
                    </Table.Td>
                    
                    <Table.Td>
                      <Text size="sm">
                        {sessionTime.room.floor.building.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {sessionTime.room.floor.name}
                      </Text>
                    </Table.Td>
                    
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {sessionTime.startTime} - {sessionTime.endTime}
                      </Text>
                    </Table.Td>
                    
                    <Table.Td>
                      <Text size="sm">
                        {formatSessionDuration(sessionTime.startTime, sessionTime.endTime)}
                      </Text>
                    </Table.Td>
                    
                    <Table.Td>
                      <Text size="sm">
                        {sessionTime.bufferMinutes > 0 ? `${sessionTime.bufferMinutes}m` : '-'}
                      </Text>
                    </Table.Td>
                    
                    <Table.Td>
                      <Badge
                        color={getSessionTimeStatusColor(sessionTime.isActive)}
                        variant="light"
                        leftSection={
                          sessionTime.isActive ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconX size={12} />
                          )
                        }
                      >
                        {sessionTime.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    
                    <Table.Td>
                      <Menu shadow="md" width={160}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => onSelectSessionTime(sessionTime)}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => onDeleteSessionTime(sessionTime.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Center py="xl">
                      <Stack align="center" gap="md">
                        <IconClock size={48} color="var(--mantine-color-dimmed)" />
                        <div>
                          <Text size="lg" fw={500} c="dimmed">
                            No session times defined
                          </Text>
                          <Text size="sm" c="dimmed" ta="center">
                            {filters.search || filters.buildingId || filters.roomId || filters.isActive !== undefined
                              ? 'Try adjusting your filters to see more results.'
                              : 'Add one to schedule doctors.'
                            }
                          </Text>
                        </div>
                      </Stack>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </Text>
          
          <Pagination
            value={pagination.page}
            onChange={handlePageChange}
            total={pagination.totalPages}
            size="sm"
          />
        </Group>
      )}
    </Stack>
  );
}
